"use client";

import { useState, useRef } from "react";
import MatchRecapModal from "./match-recap-modal";
import { useSwipePage } from "@/lib/use-swipe-page";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FeedWinEvent {
  type: "win";
  id: string;
  matchNumber: number;
  round: number;
  winnerName: string;
  loserName: string;
  winVotes: number;
  loseVotes: number;
  isTieBreaker: boolean;
  isRematch: boolean;
  winnerStreak: number;
  commentary: string | null;
  createdAt: string;
}

export interface FeedDeadlockEvent {
  type: "deadlock";
  id: string;
  matchNumber: number;
  round: number;
  nameAName: string;
  nameBName: string;
  nameAVotes: number;
  nameBVotes: number;
  createdAt: string;
}

export type FeedEvent = FeedWinEvent | FeedDeadlockEvent;

const PAGE_SIZE = 4; // 2×2 grid

// ── Compact tile ──────────────────────────────────────────────────────────────

function CompactTile({ item, onClick }: { item: FeedEvent; onClick: () => void }) {
  if (item.type === "deadlock") {
    const total = item.nameAVotes + item.nameBVotes;
    const aPct  = total > 0 ? Math.round((item.nameAVotes / total) * 100) : 50;
    return (
      <button onClick={onClick} className="w-full text-left rounded-xl bg-amber-50 border border-amber-200 p-2.5 active:bg-amber-100 transition select-none" style={{ WebkitUserSelect: "none", userSelect: "none" }}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[8px] font-black text-amber-500 uppercase tracking-wide">Rd {item.round} · ⚡ Deadlock</span>
          <span className="text-[9px] text-amber-300">#{item.matchNumber}</span>
        </div>
        <div className="space-y-0.5 mb-1.5">
          <div className="flex items-center justify-between gap-1">
            <span className="font-black text-amber-800 text-xs truncate">{item.nameAName}</span>
            <span className="text-[10px] font-black text-amber-600 shrink-0">{item.nameAVotes}</span>
          </div>
          <div className="flex items-center justify-between gap-1">
            <span className="font-black text-amber-800 text-xs truncate">{item.nameBName}</span>
            <span className="text-[10px] font-black text-amber-600 shrink-0">{item.nameBVotes}</span>
          </div>
        </div>
        {total > 0 && (
          <div className="h-1 rounded-full bg-amber-200 overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${aPct}%` }} />
          </div>
        )}
      </button>
    );
  }

  const total  = item.winVotes + item.loseVotes;
  const winPct = total > 0 ? Math.round((item.winVotes / total) * 100) : 100;
  return (
    <button onClick={onClick} className="w-full text-left rounded-xl bg-white border border-purple-100 p-2.5 active:bg-purple-50 transition select-none" style={{ WebkitUserSelect: "none", userSelect: "none" }}>
      <div className="flex items-center justify-between mb-1 gap-1">
        <span className="text-[8px] font-black text-purple-300 uppercase tracking-wide">Rd {item.round}</span>
        <div className="flex items-center gap-1">
          <span className="text-[9px] text-purple-300">#{item.matchNumber}</span>
          {item.isTieBreaker && <span className="rounded bg-yellow-100 px-1 text-[8px] font-black text-yellow-700">TB</span>}
          {item.isRematch    && <span className="rounded bg-orange-100 px-1 text-[8px] font-black text-orange-600">RM</span>}
          {item.winnerStreak >= 2 && <span className="rounded bg-purple-100 px-1 text-[8px] font-black text-purple-600">W{item.winnerStreak}</span>}
        </div>
      </div>
      <div className="space-y-0.5 mb-1.5">
        <div className="flex items-center justify-between gap-1">
          <span className="font-black text-purple-800 text-xs truncate">{item.winnerName}</span>
          <span className="text-[10px] font-black text-purple-600 shrink-0">{item.winVotes}</span>
        </div>
        <div className="flex items-center justify-between gap-1">
          <span className="text-gray-400 text-[11px] line-through truncate">{item.loserName}</span>
          <span className="text-[10px] text-gray-300 shrink-0">{item.loseVotes}</span>
        </div>
      </div>
      {total > 0 && (
        <div className="h-1 rounded-full bg-purple-100 overflow-hidden">
          <div className="h-full bg-purple-500 rounded-full" style={{ width: `${winPct}%` }} />
        </div>
      )}
    </button>
  );
}

// ── Nav button with double-tap ────────────────────────────────────────────────

function NavButton({ label, disabled, onTap, onDoubleTap, align }: {
  label: string; disabled: boolean; onTap: () => void; onDoubleTap: () => void; align: "left" | "right";
}) {
  const lastTap = useRef<number>(0);
  function handleClick() {
    if (disabled) return;
    const now = Date.now();
    if (now - lastTap.current < 350) { lastTap.current = 0; onDoubleTap(); }
    else { lastTap.current = now; onTap(); }
  }
  return (
    <button onClick={handleClick}
      className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-black transition select-none ${align === "left" ? "text-left" : "text-right"}`}
      style={{ WebkitUserSelect: "none", userSelect: "none", color: "#7c3aed", opacity: disabled ? 0.25 : 1, pointerEvents: disabled ? "none" : "auto" }}
      aria-label={label}
    >
      {label}
    </button>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function BracketMatchFeed({ feedByRound }: { feedByRound: Record<number, FeedEvent[]> }) {
  const allItems: FeedEvent[] = Object.values(feedByRound).flat().sort((a, b) => b.matchNumber - a.matchNumber);

  const totalPages = Math.max(1, Math.ceil(allItems.length / PAGE_SIZE));
  const [page, setPage] = useState(0);
  const [openMatch, setOpenMatch] = useState<number | null>(null);

  if (allItems.length === 0) return null;

  const pageItems = allItems.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const hasPrev   = page > 0;
  const hasNext   = page < totalPages - 1;

  const goNext = () => { if (hasNext) setPage((p) => p + 1); };
  const goPrev = () => { if (hasPrev) setPage((p) => p - 1); };

  // Swipe the whole feed area
  const swipe = useSwipePage({ onPrev: goPrev, onNext: goNext });

  const pageRounds = [...new Set(pageItems.map((i) => i.round))].sort((a, b) => b - a);
  const roundLabel = pageRounds.length === 1
    ? `Round ${pageRounds[0]}`
    : `Rounds ${pageRounds[pageRounds.length - 1]}–${pageRounds[0]}`;

  const MAX_DOTS = 7;
  const half     = Math.floor(MAX_DOTS / 2);
  const dotStart = Math.max(0, Math.min(page - half, totalPages - MAX_DOTS));
  const dotEnd   = Math.min(totalPages - 1, dotStart + MAX_DOTS - 1);

  return (
    <div className="flex flex-col gap-2" {...swipe}>

      {/* ── Round label + page counter ── */}
      <div className="flex items-center justify-between px-0.5">
        <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest">{roundLabel}</span>
        <span className="text-[10px] text-purple-300">{allItems.length} matches · {page + 1}/{totalPages}</span>
      </div>

      {/* ── 2×2 tile grid ── */}
      <div className="grid grid-cols-2 gap-2">
        {pageItems.map((item) => (
          <CompactTile
            key={item.type === "win" ? item.id : `tie-${item.id}`}
            item={item}
            onClick={() => setOpenMatch(item.matchNumber)}
          />
        ))}
        {pageItems.length % 2 === 1 && <div />}
      </div>

      {/* ── Nav bar: Prev · dots · Next ── */}
      <div className="flex items-center gap-1 pt-0.5">
        <NavButton label="‹ Prev" disabled={!hasPrev} align="left"
          onTap={goPrev} onDoubleTap={() => setPage(0)} />

        <div className="flex-1 flex items-center justify-center gap-1.5">
          {Array.from({ length: dotEnd - dotStart + 1 }, (_, i) => {
            const dotPage  = dotStart + i;
            const isActive = dotPage === page;
            const isFade   = (dotPage === dotStart && dotStart > 0) || (dotPage === dotEnd && dotEnd < totalPages - 1);
            return (
              <button key={dotPage} onClick={() => setPage(dotPage)}
                className="shrink-0 rounded-full transition select-none"
                style={{ WebkitUserSelect: "none", userSelect: "none", width: isActive ? 20 : 8, height: 8, background: isActive ? "#7c3aed" : "#ddd6fe", opacity: isFade ? 0.35 : 1, transition: "width 0.15s, background 0.15s, opacity 0.15s" }}
                aria-label={`Page ${dotPage + 1}`}
              />
            );
          })}
        </div>

        <NavButton label="Next ›" disabled={!hasNext} align="right"
          onTap={goNext} onDoubleTap={() => setPage(totalPages - 1)} />
      </div>

      {openMatch !== null && (
        <MatchRecapModal matchNumber={openMatch} onClose={() => setOpenMatch(null)} />
      )}
    </div>
  );
}
